import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { PREVIEW_DELAY_TIME } from '../../constants';
import { TYPE_FAVORITES } from '../../constants/channel';
import { ENUM_PLAYLIST } from '../../constants/deepSearch';
import { ENVIRONMENT_INTERNAL_EXTERNAL } from '../../constants/environment';
import { APP_IRE_PLAYER_WIDGET_ORIGIN, APP_IRE_PLAYER_WIDGET_PART_URL } from '../../constants/envs';
import { AVAILABLE_DEEP_SEARCH_TAGS } from '../../constants/label';
import { DEFAULT_FONT_FAMILY, LAYOUT_TYPE_DEFAULT } from '../../constants/lw';
import { GEN_AI_DEFAULT_VIDEO_COUNT } from '@/modules/common/constants/genAi';
import { DEFAULT_SUPPORTED_LANGUAGE } from '@/modules/common/constants/languages';
import { TAB_ENUMS } from '@/modules/common/constants/tabs';
import { ALL_SOCIAL_ENGAGEMENT_TYPES } from '@/modules/common/constants/videoEngagement';
import { APPEARANCE_MODE_LIGHT } from '@/mui/constants';

import type { WatchType } from '@/modules/common/types';
import type { ChannelType } from '@/modules/common/types/channel';
import type { VideoType } from '@/modules/common/types/video';

import { RootContainers } from '../../helpers';
import { processConfiguration } from '@/modules/common/helpers/configuration';
import { updateVideoResources } from '@/modules/common/helpers/videoCache';

type Resource = { mediaId: string; sourceUrl: string };

type InternalProps = {
  configWasLoaded: boolean;
  isFavoritesUpdating: boolean;
  adsPlayState: boolean;
  formDisplayState: boolean;
  isFavoritesVideoWasLoaded: boolean;
  additionalVideo?: VideoType;
  favoritesVideo: string[];
};

const language = DEFAULT_SUPPORTED_LANGUAGE;

export const initialState: WatchType & InternalProps = {
  appearance: APPEARANCE_MODE_LIGHT,
  ad: false,
  adHubId: '', // 2-watch
  adWidgetId: '', // test
  autoplay: true,
  autoplayMobile: true,
  autoplayNext: true,
  backMobile: false,
  basePath: '',
  bg: '',
  cDescColor: '',
  cDescSize: '',
  cInfoColor: '',
  cTitleColor: '',
  cTitleSize: '',
  cVidTitleColor: '',
  cVidTitleSize: '',
  channels: [],
  chapters: true,
  color: '',
  comments: false,
  configuration: processConfiguration({}, language),
  css: '',
  iframeRouter: true,
  dblClickFlow: true,
  deepSearch: false,
  delayPreview: PREVIEW_DELAY_TIME,
  description: '',
  dockMobile: true,
  download: false,
  dsRedirect: ENUM_PLAYLIST,
  dsTags: [...AVAILABLE_DEEP_SEARCH_TAGS],
  emailShare: true,
  engagement: [...ALL_SOCIAL_ENGAGEMENT_TYPES],
  environment: ENVIRONMENT_INTERNAL_EXTERNAL,
  excludedTags: [],
  fixedTags: [],
  font: DEFAULT_FONT_FAMILY,
  fontSize: '',
  genAI: false,
  genAICount: GEN_AI_DEFAULT_VIDEO_COUNT,
  highlights: true,
  id: 0,
  inlinePlayer: { name: '' },
  inlineVerticalPlayer: { name: '' },
  js: '',
  language,
  languages: [],
  layout: LAYOUT_TYPE_DEFAULT,
  offsetBottom: 15,
  offsetTop: 0,
  player: { name: '' },
  popupSize: 80,
  publisher: { slug: '' },
  rtl: false,
  sTitleColor: '',
  sTitleSize: '',
  sVidInfoColor: '',
  sVidInfoSize: '',
  sVidTitleColor: '',
  sVidTitleSize: '',
  search: false,
  searchLabel: '',
  searchPlaceholder: null,
  shareService: false,
  slides: false,
  socialShare: true,
  sound: false,
  soundMobile: false,
  status: 1,
  tabOrder: [...TAB_ENUMS],
  tagCount: 0,
  tagMax: 0,
  tagMin: 0,
  tagPages: [],
  tagTypes: [],
  tags: false,
  title: '',
  transcriptDownload: false,
  verticalPlayer: { name: '' },
  videoPreview: true,
  widgetOrigin: APP_IRE_PLAYER_WIDGET_ORIGIN,
  widgetPath: APP_IRE_PLAYER_WIDGET_PART_URL,

  // custom watch fields
  additionalVideo: undefined,
  adsPlayState: false,
  configWasLoaded: false,
  favoritesVideo: [],
  formDisplayState: false,
  isFavoritesUpdating: false,
  isFavoritesVideoWasLoaded: false,
};

/** https://immerjs.github.io/immer/update-patterns/ */
export const slice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    configFetchStartAction: (state) => {
      state.configWasLoaded = false;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    channelsFetchStartAction: (state, action: PayloadAction<{ cacheUsage: string; channels: ChannelType[] }>) => state,
    clearAdditionalVideoAction: (state) => {
      state.additionalVideo = undefined;
    },
    channelsFetchSuccessAction: (state, action) => {
      state.channels = action.payload;
    },
    additionalVideoFetchStartedAction: (state) => state,
    additionalVideoFetchSuccessAction: (state, action) => {
      state.additionalVideo = action.payload;
    },
    oneChannelFetchStartAction: (state) => state,
    getXRayVideoRequestAction: (state) => state,
    adsStateAction: (state, action) => {
      state.adsPlayState = action.payload;
    },
    formDisplayStateAction: (state, action) => {
      state.formDisplayState = action.payload;
    },
    updateConfigAction: (state, action) => {
      const { channels, ...main } = action.payload as WatchType;

      Object.assign(state, main);

      state.channels.push(...channels);

      RootContainers.updateCssVariables(main);

      state.configWasLoaded = true;
    },
    updateSocialMetricAction: (state, action) => {
      state.channels.forEach((channel) => {
        channel.playlist.forEach((video) => {
          if (video.mediaid === action.payload.mediaid) {
            Object.entries(action.payload.parameters).forEach(([key, value]) => {
              const item = video.performanceData.parameters.find((item$) => item$.type === key);

              if (item) {
                item.value = value as number;
              }
            });
          }
        });
      });

      if (state.additionalVideo && state.additionalVideo.mediaid === action.payload.mediaid) {
        Object.entries(action.payload.parameters).forEach(([key, value]) => {
          const item = state.additionalVideo!.performanceData.parameters.find((item$) => item$.type === key);

          if (item) {
            item.value = value as number;
          }
        });
      }
    },
    getFavoritesAction: (state) => {
      state.isFavoritesUpdating = true;
    },
    setFavoritesAction: (state, action) => {
      state.isFavoritesVideoWasLoaded = true;
      state.isFavoritesUpdating = false;
      state.favoritesVideo = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addFavoritesAction: (state, action: PayloadAction<VideoType['mediaid']>) => {
      state.isFavoritesUpdating = true;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteFavoritesAction: (state, action: PayloadAction<VideoType['mediaid']>) => {
      state.isFavoritesUpdating = true;
    },
    cancelFavoritesUpdatingAction: (state) => {
      state.isFavoritesUpdating = false;
    },
    updateFavoritesChannelAction: (state) => {
      const favoritesChannel = state.channels.find((channel) => channel.type === TYPE_FAVORITES) as ChannelType;

      if (favoritesChannel) {
        favoritesChannel.playlist = favoritesChannel.playlist.filter(({ mediaid }) =>
          state.favoritesVideo.includes(mediaid),
        );
      }
    },
    updateResourcesStartedAction: (state, action: PayloadAction<Resource[]>) => {
      if (action.payload) {
        return state;
      }

      return state;
    },
    updateResourceAction: (state, action) => {
      state.channels.forEach((channel) => {
        channel.playlist.forEach((video) => {
          updateVideoResources(video, action.payload);
        });
      });
    },
    setLanguageAction(state, action: PayloadAction<WatchType['language']>) {
      state.language = action.payload;
    },
    resetStoreAction: (state) => state,
  },
});

export const {
  setLanguageAction,
  resetStoreAction,
  configFetchStartAction,
  updateConfigAction,
  channelsFetchStartAction,
  channelsFetchSuccessAction,
  adsStateAction,
  formDisplayStateAction,
  clearAdditionalVideoAction,
  updateSocialMetricAction,
  oneChannelFetchStartAction,
  getXRayVideoRequestAction,
  additionalVideoFetchStartedAction,
  additionalVideoFetchSuccessAction,
  getFavoritesAction,
  setFavoritesAction,
  addFavoritesAction,
  deleteFavoritesAction,
  cancelFavoritesUpdatingAction,
  updateFavoritesChannelAction,
  updateResourcesStartedAction,
  updateResourceAction,
} = slice.actions;
